/* eslint-disable react/jsx-no-duplicate-props */
import React, { useEffect, useMemo, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import { SelectWithFilter } from '@utmost.co/utmost-design-system';
import { FlagIcon } from '@utmost.co/utmost-design-system/flags';
import { AtomProps } from '../types';
import {
  CountryCallingCode,
  CountryCode,
  DEFAULT_CALLING_CODE,
  DEFAULT_COUNTRY_CODE,
  InternationalPhoneNumber,
  InternationalPhoneNumberModel
} from '../../forms/utils/validation';
import { Label } from '../Label/Label';
import { HelpText } from '../HelpText/HelpText';
import { disabledAutoComplete, useAnonymousName } from '../utils/disableBrowserAutocomplete';
import { PermissionHelper } from '../common/PermissionHelper';
import { FieldPermission } from '../../forms/controllers/SmartformContext';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';

const options = InternationalPhoneNumber.getMenuItems().map((item) => ({
  ...item,
  icon: <FlagIcon country={item.value} />
}));

export interface InternationalPhoneNumberValue {
  countryIsoCode?: CountryCode;
  isPossible?: boolean;
  isValid?: boolean;
  isEmpty?: boolean;
  model?: InternationalPhoneNumberModel;
}

interface InternationalPhoneNumberAtomProps extends AtomProps<InternationalPhoneNumberValue> {
  onModelInit?(isValid: boolean): void;
  onFocus?(): void;
  permission?: FieldPermission;
  allowedCountryCodes?: string[];
}

export const InternationalPhoneNumberAtom: React.FC<React.PropsWithChildren<InternationalPhoneNumberAtomProps>> = ({
  value,
  label,
  error,
  dataTest,
  helpText,
  infoText,
  disabled,
  onChange,
  onBlur,
  onFocus,
  required,
  placeholder,
  autoFocus,
  permission,
  onModelInit,
  allowedCountryCodes
}) => {
  const { permissionDebugger } = useSmartFormContext();
  const isRestricted = !!allowedCountryCodes?.length;
  const filteredOptions = useMemo(
    () => (isRestricted ? options.filter((option) => allowedCountryCodes!.includes(option.value)) : options),
    [isRestricted, allowedCountryCodes]
  );
  const defaultCountryCode = useMemo<CountryCode>(
    () =>
      isRestricted && !allowedCountryCodes!.includes(DEFAULT_COUNTRY_CODE)
        ? (allowedCountryCodes![0] as CountryCode)
        : DEFAULT_COUNTRY_CODE,
    [isRestricted, allowedCountryCodes]
  );
  const [selectedCallingCode, setSelectedCallingCode] = useState<CountryCallingCode>(() =>
    InternationalPhoneNumber.getCallingCode(defaultCountryCode)
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>(defaultCountryCode);
  const [initialized, setInitialized] = useState(false);
  const [nationalNumber, setNationalNumber] = useState<string>('');
  const idTextField = useAnonymousName(`${dataTest}-phone-number`);

  useEffect(() => {
    if (value?.model) {
      const details = new InternationalPhoneNumber(value.model).getDetails();
      if (!initialized) {
        setInitialized(true);
        // move onModelInit to next microtask otherwise setInitialized wont be set due to react state batching
        setTimeout(() => {
          onModelInit?.(details.isValid);
        }, 0);
      }
      if (details.isValid) {
        setSelectedCountryCode(details.country ?? DEFAULT_COUNTRY_CODE);
        setSelectedCallingCode(details.countryCallingCode ?? (DEFAULT_CALLING_CODE as CountryCallingCode));
      }
      setNationalNumber(details.internationalFormat.nationalNumber);
    } else {
      setNationalNumber('');
    }
  }, [value, initialized, onModelInit]);

  const countryCodeChange = (countryCode: CountryCode) => {
    const currentCallingCode = InternationalPhoneNumber.getCallingCode(countryCode);
    setSelectedCountryCode(countryCode);
    setSelectedCallingCode(currentCallingCode);
    applyChanges(currentCallingCode, nationalNumber, countryCode, 'both');
  };

  const phoneNumberBlur = (newNationalNumber: string = nationalNumber) => {
    applyChanges(selectedCallingCode, newNationalNumber, selectedCountryCode, 'blur');
  };

  const phoneNumberChange = (newNationalNumber: string = '') => {
    const numberWithoutLetters = newNationalNumber.replace(/[^0-9 ]/g, '');
    setNationalNumber(numberWithoutLetters);
    applyChanges(selectedCallingCode, numberWithoutLetters, selectedCountryCode, 'change');
  };

  const applyChanges = (
    countryCallingCode: CountryCallingCode,
    newNationalNumber: string,
    countryIsoCode: CountryCode,
    eventType: 'blur' | 'change' | 'both'
  ) => {
    const details = new InternationalPhoneNumber({
      number: newNationalNumber,
      countryCode: countryCallingCode as string,
      areaCode: ''
    }).getDetails();

    const handlers: AtomProps<InternationalPhoneNumberValue>['onBlur'][] = [];
    if (['change', 'both'].includes(eventType)) {
      handlers.push(onChange);
    }
    if (['blur', 'both'].includes(eventType)) {
      handlers.push(onBlur);
    }

    handlers.forEach((handler) => {
      handler?.({
        model: details.model,
        countryIsoCode,
        isEmpty: details.isEmpty,
        isPossible: details.isPossible,
        isValid: details.isValid
      });
    });
  };

  return (
    <FormControl
      error={!!error}
      required={required}
      disabled={disabled}
      fullWidth
      data-test={`${dataTest}-field-control`}
    >
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <Label
        text={label}
        dataTest={dataTest}
        infoText={infoText}
        required={required}
        htmlFor={idTextField}
        disabled={disabled}
      />
      <Grid container spacing={2}>
        <Grid item>
          <SelectWithFilter<CountryCode>
            value={selectedCountryCode}
            options={filteredOptions}
            required
            className="id_phone-number-code"
            disabled={disabled}
            hideCodes
            onChange={countryCodeChange}
            dataTest={`${dataTest}-select-country`}
            style={{ width: 130 }}
            menuProps={{
              anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
              transformOrigin: { horizontal: 'left', vertical: 'top' }
            }}
          />
        </Grid>
        <Grid item xs>
          <TextField
            value={nationalNumber}
            type="text"
            onBlur={(e) => phoneNumberBlur(e.target.value)}
            onFocus={onFocus}
            fullWidth
            disabled={disabled}
            autoFocus={autoFocus}
            id={idTextField}
            name={idTextField}
            placeholder={placeholder}
            data-test={`${dataTest}-phone-number`}
            InputProps={{
              disableUnderline: true,
              error: !!error,
              'aria-invalid': !!error,
              ...disabledAutoComplete
            }}
            inputProps={{
              className: `id_phone-number`
            }}
            onChange={(e) => {
              phoneNumberChange(e.target.value);
            }}
          />
        </Grid>
      </Grid>
      <HelpText text={helpText} error={error} dataTest={dataTest} hideWhenEmpty />
      {permissionDebugger && <PermissionHelper permission={permission} />}
    </FormControl>
  );
};
